import { ClosedReason, Dtos, type Rate, SuretyQuoteStatus } from "@inrev/common";
import { useAdminFetchAgencyPreviews } from "@portal/api/agency/admin";
import {
	useAdminUnderwriteQuote,
	useAdminUpdateBondRequestStatus,
} from "@portal/api/bond-request/admin";
import { AdminBondRequest } from "@portal/api/bond-request/admin/types";
import { UseFollowersReturn } from "@portal/api/followers/types";
import { EntityTags } from "@portal/components/admin/EntityTags";
import { ConfirmationModal } from "@portal/components/layout/ConfirmationModal";
import { Modal } from "@portal/components/layout/Modal";
import { cn } from "@portal/components/lib/utils";
import { Button, Icon, Spinner, Text } from "@portal/components/primitives";
import { ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatDisplayTitle } from "../../../utils";
import { ClosedReasonModal } from "../../shared/account/ClosedReasonModal";
import { AdminAssigneeInlineControls } from "./AdminAssigneeInlineControls";
import {
	AdminBondRequestStatusTransitionDropdown,
	QuoteStatusUpdate,
} from "./AdminBondRequestStatusTransitionDropdown";
import { AdminChangeBondStatusModal } from "./AdminChangeBondStatusModal";
import { AdminFollowerControls } from "./AdminFollowerControls";
import { isBondFormAvailable } from "./bondFormAvailability";

type AdminBondRequestCardHeaderProps = {
	request: AdminBondRequest;
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response | undefined;
	bondFormIsFetching?: boolean;
	bondFormError?: Error | null;
	accountRate: Rate | undefined;
	accountDisplayName: string;
	actionButtons?: ReactNode;
	followersControl: UseFollowersReturn;
	isComplete: boolean;
	canApprove: boolean;
	/** Root padding. The tabbed layout renders the header flush against the page and supplies its own. */
	className?: string;
};

const AdminBondRequestHeaderMeta = ({
	request,
	accountDisplayName,
	agencyName,
}: {
	request: AdminBondRequest;
	accountDisplayName: string;
	agencyName: string | undefined;
}) => {
	const segments: { id: string; label: string; content: ReactNode }[] = [];

	if (accountDisplayName !== request.principal.name) {
		segments.push({
			id: "account",
			label: "Account",
			content: (
				<Link
					to={`/accounts/${request.suretyAccountId}`}
					className="text-brand-accent font-medium hover:underline"
				>
					{accountDisplayName}
				</Link>
			),
		});
	}
	segments.push({ id: "agency", label: "Agency", content: agencyName });
	if (request.agentName) {
		segments.push({ id: "agent", label: "Agent", content: request.agentName });
	}

	return (
		<Text
			type="caption"
			color="secondary"
			className="flex flex-wrap items-center gap-x-[6px] gap-y-[2px]"
			data-testid="admin-bond-request-header-meta"
		>
			{segments.map(({ id, label, content }) => (
				<Text type="caption" color="primary" className="flex items-center gap-x-[6px]" key={id}>
					<Text color="secondary">{label}:</Text>
					{content}
				</Text>
			))}
		</Text>
	);
};

const isUpdatableStatus = (status: SuretyQuoteStatus): status is QuoteStatusUpdate => {
	const updatableStatuses: QuoteStatusUpdate[] = [
		"approved",
		"declined",
		"review",
		"closed",
		"accepted",
		"pending",
	];
	return updatableStatuses.includes(status as QuoteStatusUpdate);
};

export const AdminBondRequestCardHeader = ({
	request,
	bondForm,
	bondFormIsFetching = false,
	bondFormError = null,
	accountRate,
	accountDisplayName,
	actionButtons,
	followersControl,
	isComplete,
	canApprove,
	className,
}: AdminBondRequestCardHeaderProps) => {
	const { agencyNameMap } = useAdminFetchAgencyPreviews();
	const [isChangeBondStatusModalOpen, setIsChangeBondStatusModalOpen] = useState(false);
	const [localStatus, setLocalStatus] = useState<SuretyQuoteStatus>(request.status);
	const [runModelConfirmOpen, setRunModelConfirmOpen] = useState(false);
	const { updateBondRequestStatus, updateBondRequestStatusLoading, updateBondRequestStatusError } =
		useAdminUpdateBondRequestStatus(request.id, request.suretyAccountId);
	const { underwriteQuote, underwriteQuoteIsLoading } = useAdminUnderwriteQuote(
		request.suretyAccountId,
		request.id,
	);

	const closeBondRequest = ({
		closedReason,
		note,
		onSuccess,
	}: {
		closedReason: ClosedReason;
		note?: string;
		onSuccess?: () => void;
	}) => {
		updateBondRequestStatus({
			data: { suretyType: "contract", status: "closed", closedReason, note },
			onSuccess,
		});
	};

	const changeStatus = ({ data }: { data: Dtos.Admin.SuretyQuote.UpdateStatus.Request }) => {
		updateBondRequestStatus({ data });
	};

	const bondFormAvailable = isBondFormAvailable(bondForm, bondFormError);

	useEffect(() => {
		setLocalStatus(request.status);
	}, [request.status]);

	useEffect(() => {
		if (!isChangeBondStatusModalOpen || localStatus !== "approved" || bondFormAvailable) {
			return;
		}
		setIsChangeBondStatusModalOpen(false);
		setLocalStatus(request.status);
	}, [bondFormAvailable, isChangeBondStatusModalOpen, localStatus, request.status]);

	const handleChangeBondStatus = (newStatus: SuretyQuoteStatus) => {
		setIsChangeBondStatusModalOpen(true);
		setLocalStatus(newStatus);
	};

	return (
		<div className={cn("w-full h-fit flex space-x-[50px]", className)}>
			{/*
			 * Every detail row under the title is 12px — `caption` text, the chip labels, and the tag
			 * pills — so the block reads as one unit rather than a staircase of sizes.
			 *
			 * Two spacing rules, and they need different numbers to look the same. `Text` trims its box
			 * to cap height, so a gap between two text lines renders at exactly the margin. The chip
			 * rows do not: the 24px avatar/pill and 22px tag boxes overhang their label's cap by ~6px
			 * and ~5px, so an equal margin reads as an unequal gap. The chip margins below are
			 * compensated to land on the same ~10px of optical air as the text rows.
			 */}
			<div className="flex-1 flex flex-col">
				<div className="flex items-start space-x-[15px]">
					<div className="flex flex-col space-y-[12px]">
						<Text type="display" color="primary">
							{`${formatDisplayTitle(request.displayTitle)} Request`}
						</Text>
						<div className="flex flex-col space-y-[10px] py-[2px]">
							<Text type="body" color="primary">
								for{" "}
								<Link
									to={`/accounts/${request.suretyAccountId}`}
									className="text-brand-accent font-medium hover:underline"
								>
									{request.principal.name}
								</Link>
							</Text>
							<AdminBondRequestHeaderMeta
								request={request}
								accountDisplayName={accountDisplayName}
								agencyName={agencyNameMap[request.agencyId]}
							/>
						</div>
					</div>
				</div>
				<div className="flex space-x-[15px] mt-[4px]">
					<AdminAssigneeInlineControls
						className="pt-0"
						assignedEntity={{ entityType: "quote", entityId: request.id }}
						currentAssignee={request.underwriterUserId}
						trackingId="bond-request-admin-assign-underwriter"
					/>
					<AdminFollowerControls
						{...followersControl}
						className="pt-0"
						manageFollowersSubtext="Followers will receive email notifications for all comments and changes to the status of this bond request"
						agencyId={request.agencyId}
						trackingPrefix="bond-request-admin-header-follow"
					/>
				</div>
				<div className="mt-[5px]">
					<EntityTags
						entityType="quote"
						entityId={request.id}
						accountId={request.suretyAccountId}
					/>
				</div>
			</div>
			<div>
				<div className="flex flex-col items-stretch w-[200px] mb-[15px] space-y-[10px]">
					<AdminBondRequestStatusTransitionDropdown
						currentStatus={localStatus}
						onStatusChange={handleChangeBondStatus}
						isLoading={updateBondRequestStatusLoading}
						isComplete={isComplete}
						canApprove={canApprove}
						bondFormAvailable={bondFormAvailable}
						bondFormIsFetching={bondFormIsFetching}
						bondFormError={bondFormError}
						trackingPrefix="bond-request-admin-status"
					/>
					{request.status === "closed" && request.closedReason && (
						<div
							className="w-full break-all pr-1 text-center text-[13px] text-tertiary italic"
							title={
								request.closedNote ? `${request.closedReason} (${request.closedNote})` : undefined
							}
						>
							{request.closedReason}
							{request.closedNote && ` (${request.closedNote})`}
						</div>
					)}
					<div className="w-full flex flex-col space-y-[5px]">
						<Button
							variant="outline"
							color="secondary"
							className="w-full"
							leadingIcon={<Icon type="arrow-path" color="secondary" size="mini" />}
							onClick={() => setRunModelConfirmOpen(true)}
							loading={underwriteQuoteIsLoading}
							trackingId="bond-request-admin-run-model"
						>
							Run Model
						</Button>
						{actionButtons && actionButtons}
					</div>
					{isChangeBondStatusModalOpen && isUpdatableStatus(localStatus) && (
						<>
							{localStatus === "closed" ? (
								<ClosedReasonModal
									closeFunction={closeBondRequest}
									closeFunctionError={updateBondRequestStatusError}
									isClosing={updateBondRequestStatusLoading}
									onDismiss={() => {
										setIsChangeBondStatusModalOpen(false);
										setLocalStatus(request.status);
									}}
									onSuccess={() => setIsChangeBondStatusModalOpen(false)}
									closingAction="close"
								/>
							) : localStatus === "approved" && !bondFormAvailable ? null : (
								<AdminChangeBondStatusModal
									request={request}
									bondForm={bondForm}
									accountRate={accountRate}
									onUpdate={changeStatus}
									onClose={(status) => {
										setIsChangeBondStatusModalOpen(false);
										setLocalStatus(status ?? request.status);
									}}
									status={localStatus}
								/>
							)}
						</>
					)}
				</div>
			</div>
			{updateBondRequestStatusLoading && (
				<Modal>
					<Spinner />
				</Modal>
			)}
			{runModelConfirmOpen && (
				<ConfirmationModal
					message="Are you sure you want to run the model?"
					onConfirm={() => {
						underwriteQuote();
						setRunModelConfirmOpen(false);
					}}
					onCancel={() => setRunModelConfirmOpen(false)}
				/>
			)}
		</div>
	);
};
